import { useState } from "react";
import {
  BookOpen,
  Bot,
  CalendarDays,
  Check,
  Columns2,
  Gift,
  Handshake,
  Search,
  Share2,
  UserRound,
} from "lucide-react";
import { LayoutGroup, motion } from "framer-motion";
import { JOURNEY_STEPS } from "../../constants/simmarkets";
import { ArrowIcon, SectionShell, useFade } from "./ui";

const ICONS = {
  search: Search,
  book: BookOpen,
  user: UserRound,
  bot: Bot,
  columns: Columns2,
  check: Check,
  calendar: CalendarDays,
  share: Share2,
  handshake: Handshake,
  gift: Gift,
};

function MarketJourney() {
  const { fade, reduce } = useFade();
  const [step, setStep] = useState(0);
  const active = JOURNEY_STEPS[step];
  const Icon = ICONS[active.icon] || Search;
  const last = JOURNEY_STEPS.length - 1;
  const go = (index) => setStep(Math.max(0, Math.min(last, index)));

  const onTrackKeyDown = (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      go(step + 1);
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      go(step - 1);
    }
    if (event.key === "Home") {
      event.preventDefault();
      go(0);
    }
    if (event.key === "End") {
      event.preventDefault();
      go(last);
    }
  };

  return (
    <SectionShell id="journey" labelledBy="journey-heading" tone="white">
      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.header
          className="max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            How SimMarket works
          </p>
          <h2
            id="journey-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            A simple yet intelligent
            <span className="mt-1 block">customer journey</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
            This intelligent workflow creates a seamless experience that benefits both consumers and
            financial institutions.
          </p>
        </motion.header>

        <motion.div
          className="mt-12"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <LayoutGroup>
            <ol
              className="grid gap-2 sm:grid-cols-5"
              onKeyDown={onTrackKeyDown}
              aria-label="SimMarket customer journey"
            >
              {JOURNEY_STEPS.map((item, index) => {
                const selected = index === step;
                return (
                  <li key={item.n}>
                    <button
                      type="button"
                      onClick={() => go(index)}
                      className={`relative flex min-h-[5.5rem] w-full cursor-pointer flex-col items-start border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold ${
                        selected
                          ? "border-market-gold bg-market-gold text-navy"
                          : "border-navy/10 bg-[#f3f7f5] text-navy hover:border-market-green/50"
                      }`}
                      aria-current={selected ? "step" : undefined}
                    >
                      {selected ? (
                        <motion.span
                          layoutId="market-journey-bar"
                          className="absolute inset-x-0 top-0 h-[3px] bg-market-green"
                          transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 34 }}
                        />
                      ) : null}
                      <span className={`font-mono text-[11px] tracking-[0.16em] ${selected ? "text-navy/50" : "text-market-green"}`}>
                        {item.n}
                      </span>
                      <span className="mt-1 text-sm font-semibold tracking-tight">{item.title}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </LayoutGroup>

          <div className="mt-4 flex items-stretch gap-px overflow-hidden border border-navy/10 bg-navy/10 lg:grid lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="bg-navy px-6 py-8 text-white sm:px-8 sm:py-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-market-gold">
                Step {active.n} — {active.title}
              </p>
              <div className="mt-5 flex items-start gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-market-green text-white">
                  <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                </span>
                <p className="max-w-2xl text-lg font-semibold leading-snug tracking-tight sm:text-xl">
                  {active.copy}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white px-5 py-4">
              <button
                type="button"
                onClick={() => go(step - 1)}
                disabled={step === 0}
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center border border-navy/15 text-navy transition hover:border-navy disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold"
                aria-label="Previous step"
              >
                <span className="rotate-180">
                  <ArrowIcon />
                </span>
              </button>
              <button
                type="button"
                onClick={() => go(step + 1)}
                disabled={step === last}
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center border border-navy/15 text-navy transition hover:border-navy disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold"
                aria-label="Next step"
              >
                <ArrowIcon />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </SectionShell>
  );
}

export default MarketJourney;
